import React, { useState, useEffect } from 'react';
import { ref, push, set, update } from 'firebase/database';
import { auth, database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange
} from '../../../../services/nationalizationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD
  || process.env.REACT_APP_BACKEND_URL
  || "http://localhost:8000";

const ReviewRequestsModal = ({ employeeId, companyUID, employeeName, requests, onClose, onSuccess }) => {
  const { t } = useTranslation('myteam');
  const { isRTL, language } = useLanguage();
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');
  const [processing, setProcessing] = useState(null);
  const pendingList = requests.filter(r => r.status === 'pending_hr');

  const handleAction = async (req, action) => {
    if (!companyUID || !employeeId) return;
    setProcessing(req.id);

    try {
      const basePath = `companies/${companyUID}/employees/${employeeId}`;
      const status = action === 'approve' ? 'approved' : 'rejected';

      // 1. Update request status
      await update(ref(database, `${basePath}/requests/${req.id}`), {
        status,
        reviewedAt: Date.now(),
        reviewedBy: 'HR'
      });

      if (status === 'approved') {
        // 2. Apply changes based on type
        if (req.type === 'salary') {
          const workforceUpdates = {
            [`${basePath}/compensation/base`]: Number(req.amount)
          };
          const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
            companyUID,
            employeeId,
            source: 'employee_salary_change',
            changeKinds: ['compensation']
          });
          await update(ref(database), workforceUpdates);
          try {
            await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
              eventId: lifecycleEvent.eventId,
              source: 'employee_salary_change',
              changeKinds: ['compensation']
            });
          } catch (syncError) {
            console.warn('Nationalization salary review is pending:', syncError);
          }
          announceNationalizationWorkforceChange({
            companyUID,
            source: 'employee_salary_change',
            recalculated: false
          });
        } else if (req.type === 'bonus') {
          const bonusRef = push(ref(database, `${basePath}/compensation/bonuses`));
          const source = 'employee_bonus_approval';
          const workforceUpdates = {
            [`${basePath}/compensation/bonuses/${bonusRef.key}`]: {
              amount: Number(req.amount),
              reason: req.reason,
              date: Date.now(),
              approvedBy: 'HR'
            }
          };
          const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
            companyUID,
            employeeId,
            source,
            changeKinds: ['compensation']
          });
          await update(ref(database), workforceUpdates);
          let lifecycleSync = null;
          try {
            lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
              eventId: lifecycleEvent.eventId,
              source,
              changeKinds: ['compensation']
            });
          } catch (syncError) {
            console.warn('Nationalization bonus review is pending:', syncError);
          }
          announceNationalizationWorkforceChange({
            companyUID,
            source,
            recalculated: false,
            syncPending: !lifecycleSync
          });
        } else if (req.type === 'offboarding') {
          const workforceUpdates = {
            [`${basePath}/lifecycle/status`]: 'offboarding',
            [`${basePath}/lifecycle/terminationDate`]: req.date,
            [`${basePath}/status`]: 'offboarding'
          };
          const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
            companyUID,
            employeeId,
            source: 'employee_offboarding_approval',
            changeKinds: ['lifecycle']
          });
          await update(ref(database), workforceUpdates);
          let lifecycleSync = null;
          try {
            lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
              eventId: lifecycleEvent.eventId,
              source: 'employee_offboarding_approval',
              changeKinds: ['lifecycle']
            });
          } catch (syncError) {
            console.warn('Nationalization offboarding sync is pending:', syncError);
          }
          announceNationalizationWorkforceChange({
            companyUID,
            source: 'employee_offboarding_approval',
            recalculated: false,
            syncPending: !lifecycleSync
          });
        }
      }

      // 3. Notify Employee
      const notifRef = push(ref(database, `${basePath}/notifications`));
      const requestTypeLabel = t(`requestsModal.types.${req.type}`, req.type);
      const title = t(`requestsModal.notificationTitles.${status}`, status);
      const message = t(`requestsModal.notificationMessages.${status}`, {
        type: requestTypeLabel,
        defaultValue: `Your ${requestTypeLabel} request has been ${status}.`
      });
      await set(notifRef, {
        title,
        message,
        type: status === 'approved' ? 'success' : 'error',
        timestamp: Date.now(),
        read: false
      });

      // 4. Send Email Notification to Employee
      try {
        // The backend resolves the employee address itself. The browser supplies
        // only the tenant/employee IDs and its verified Firebase identity.
        const token = await auth.currentUser?.getIdToken();
        await fetch(`${BACKEND_URL}/send-notification-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            companyId: companyUID,
            employeeId,
            subject: title,
            message,
            type: status === 'approved' ? 'success' : 'error'
          })
        });
      } catch (e) {
        console.error("Employee email send failed", e);
      }

      // 5. Notify the Manager who made the original request
      if (req.requestedBy) {
        try {
          const token = await auth.currentUser?.getIdToken();
          await fetch(`${BACKEND_URL}/notify-manager-hr-decision`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              companyId: companyUID,
              employeeId,
              managerId: req.requestedBy,
              requestType: req.type,
              decision: status,
              employeeName,
              locale: language === 'ar' ? 'ar' : 'en'
            })
          });
        } catch (e) {
          console.error("Manager notification failed", e);
        }
      }

      onSuccess?.();
    } catch (e) {
      console.error('Action failed', e);
      alert(t('requestsModal.actionFailed', 'Action failed'));
    } finally {
      setProcessing(null);
    }
  };

  useEffect(() => {
    if (pendingList.length === 0) {
      const timer = setTimeout(onClose, 1000);
      return () => clearTimeout(timer);
    }
  }, [pendingList.length, onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '600px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('requestsModal.title', { name: safeEmployeeName, defaultValue: `Review Requests for ${safeEmployeeName}` })}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {pendingList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--app-heading, #031128)', opacity: 0.6 }}>
              <i className="fa-solid fa-check-circle" style={{ fontSize: 32, marginBottom: 16, display: 'block', color: 'var(--app-tone-40eb6e, #40EB6E)' }}></i>
              <span style={{ fontFamily: 'Montserrat', fontSize: '16px' }}>{t('requestsModal.allReviewed', 'All requests reviewed!')}</span>
            </div>
          ) : pendingList.map(req => (
            <div key={req.id} style={{
              background: '#F8F9FA',
              padding: 20,
              borderRadius: 12,
              marginBottom: 16,
              border: '1px solid #E0E0E0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  color: '#568ded',
                  fontFamily: 'Montserrat',
                  fontSize: '16px'
                }}>
                  {t('requestsModal.requestType', { type: req.type, defaultValue: `${req.type} Request` })}
                </span>
                <span style={{
                  opacity: 0.7,
                  fontSize: '13px',
                  fontFamily: 'Montserrat',
                  color: 'var(--app-heading, #031128)'
                }}>
                  {new Date(req.requestedAt).toLocaleDateString()}
                </span>
              </div>

              {req.amount && (
                <div style={{ marginBottom: 8, fontFamily: 'Montserrat', color: 'var(--app-heading, #031128)' }}>
                  {t('requestsModal.requestedAmount', 'Requested Amount:')} <strong style={{ color: 'var(--app-tone-40eb6e, #40EB6E)' }}>${Number(req.amount).toLocaleString()}</strong>
                </div>
              )}
              {req.date && (
                <div style={{ marginBottom: 8, fontFamily: 'Montserrat', color: 'var(--app-heading, #031128)' }}>
                  {t('requestsModal.proposedDate', 'Proposed Date:')} <strong>{req.date}</strong>
                </div>
              )}
              {req.reason && (
                <div style={{
                  marginBottom: 12,
                  fontStyle: 'italic',
                  background: '#FFFFFF',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #E0E0E0',
                  fontFamily: 'Montserrat',
                  color: 'var(--app-heading, #031128)'
                }}>
                  "{req.reason}"
                </div>
              )}
              {req.requestedByName && (
                <div style={{
                  fontSize: '13px',
                  color: '#666',
                  fontFamily: 'Montserrat',
                  marginBottom: 16
                }}>
                  <i className="fa-solid fa-user" style={{ marginRight: 6 }}></i>
                  {t('requestsModal.requestedBy', { name: req.requestedByName || '', defaultValue: `Requested by: ${req.requestedByName}` })}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleAction(req, 'reject')}
                  disabled={processing === req.id}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: processing === req.id ? '#CCCCCC' : '#FFFFFF',
                    border: '2px solid #EB4040',
                    borderRadius: '8px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--app-tone-eb4040, #EB4040)',
                    cursor: processing === req.id ? 'not-allowed' : 'pointer'
                  }}
                  data-light-control={processing === req.id ? "quiet-danger" : "quiet-danger"}>
                  <i className="fa-solid fa-times" style={{ marginRight: 6 }}></i>
                  {t('requestsModal.reject', 'Reject')}
                </button>
                <button
                  onClick={() => handleAction(req, 'approve')}
                  disabled={processing === req.id}
                  style={{ '--light-control-fill': processing === req.id ? '#CCCCCC' : '#40EB6E',
                    padding: '10px 20px',
                    backgroundColor: processing === req.id ? '#CCCCCC' : '#40EB6E',
                    border: 'none',
                    borderRadius: '8px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: processing === req.id ? 'var(--app-text, #FFFFFF)' : "#FFFFFF",
                    cursor: processing === req.id ? 'not-allowed' : 'pointer'
                  }}
                  data-light-control={processing === req.id ? "quiet" : "filled"}>
                  <i className="fa-solid fa-check" style={{ marginRight: 6 }}></i>
                  {t('requestsModal.approve', 'Approve')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewRequestsModal;
